import { computed } from 'vue';
import { useStore } from 'dashboard/composables/store';

export const BILLING_FEATURES = Object.freeze({
  agents: 'agents',
  inboxes: 'inboxes',
  labels: 'labels',
  aiAgents: 'ai_agents',
  aiPlans: 'ai_plans',
  contactImport: 'contact_import',
  contactExport: 'contact_export',
  companyImport: 'company_import',
  companyExport: 'company_export',
  scheduledMessages: 'scheduled_messages',
  tasksPerEntity: 'tasks_per_entity',
  // Cota unica da feature de Automacoes (#723): fluxo, macro, automacao e rotina
  // disputam este balde. As tres chaves abaixo continuam existindo (contratos
  // assinados as referenciam em limit_overrides), mas ja nao limitam nada.
  automationFlows: 'automation_flows',
  automations: 'automations',
  botFlows: 'bot_flows',
  macros: 'macros',
  routines: 'routines',
  cannedResponses: 'canned_responses',
  massOutboundCampaigns: 'mass_outbound_campaigns',
  massOutboundContacts: 'mass_outbound_contacts',
  websiteCampaigns: 'website_campaigns',
  smsCampaigns: 'sms_campaigns',
  smsCampaignContacts: 'sms_campaign_contacts',
  teams: 'teams',
  customAttributes: 'custom_attributes',
  sla: 'sla',
  leadgen: 'leadgen',
});

const BLOCKED_STATES = ['limit_reached', 'expired', 'blocked', 'admin_only'];

const hasEntitlementPayload = entitlement => {
  return !!entitlement && Object.keys(entitlement).length > 0;
};

const finiteNumber = value => {
  if (value === null || value === undefined || value === '') return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const quantityExceedsLimit = (entitlement, { quantity = 1, usage } = {}) => {
  const limit = finiteNumber(entitlement?.limit);
  if (limit === null) return false;

  const currentUsage = finiteNumber(usage ?? entitlement?.usage) || 0;
  const requestedQuantity = finiteNumber(quantity) ?? 1;

  return currentUsage + requestedQuantity > limit;
};

export const getBillingFeature = (store, featureKey) => {
  const getter = store.getters['billingEntitlements/getFeature'];

  return typeof getter === 'function' ? getter(featureKey) : {};
};

export const isBillingFeatureBlocked = (entitlement, options = {}) => {
  if (!hasEntitlementPayload(entitlement)) return false;
  if (entitlement.allowed === false) return true;
  if (BLOCKED_STATES.includes(entitlement.state)) return true;

  return quantityExceedsLimit(entitlement, options);
};

const entitlementForModal = (entitlement, featureKey, options = {}) => {
  const fallback = {
    key: featureKey,
    state: 'limit_reached',
    allowed: false,
    reason: 'limit_reached',
    ...(options.fallback || {}),
  };

  if (!hasEntitlementPayload(entitlement)) return fallback;
  if (!quantityExceedsLimit(entitlement, options)) return entitlement;

  return {
    ...entitlement,
    usage: options.usage ?? entitlement.usage,
    state: 'limit_reached',
    allowed: false,
    reason: 'limit_reached',
  };
};

export const openBillingLimitModal = (store, featureKey, options = {}) => {
  const entitlement = getBillingFeature(store, featureKey);

  store.dispatch(
    'billingEntitlements/openLimitModal',
    entitlementForModal(entitlement, featureKey, options)
  );
};

export const ensureBillingFeature = (store, featureKey, options = {}) => {
  const entitlement = getBillingFeature(store, featureKey);

  if (!isBillingFeatureBlocked(entitlement, options)) return true;

  openBillingLimitModal(store, featureKey, options);
  return false;
};

export const refreshBillingEntitlements = store => {
  return store.dispatch('billingEntitlements/fetch');
};

export const useBillingEntitlements = () => {
  const store = useStore();
  const features = computed(
    () => store.getters['billingEntitlements/getFeatures'] || {}
  );

  return {
    features,
    getFeature: featureKey => getBillingFeature(store, featureKey),
    ensureFeature: (featureKey, options) =>
      ensureBillingFeature(store, featureKey, options),
    openLimitModal: (featureKey, options) =>
      openBillingLimitModal(store, featureKey, options),
    refreshEntitlements: () => refreshBillingEntitlements(store),
  };
};
